import type { GoogleFontImport } from './googleFonts';

type CssValue = string | number;

export type SubtitleStyleDefinition = {
  fontFamily?: string;
  fontSize?: number;
  lineHeight?: CssValue;
  color?: string;
  textAlign?: 'left' | 'center' | 'right';

  padding?: {
    vertical?: number;
    horizontal?: number;
  } | null;

  box?: {
    backgroundColor?: string;
    borderRadius?: number;
    blur?: number;
  } | null;
};

// Reproduces the modern-inline subtitle appearance previously hardcoded in the
// render engine (SubtitleElement). Must stay pixel-identical so existing
// projects do not shift when no style is selected.
export const DEFAULT_SUBTITLE_STYLE: Readonly<SubtitleStyleDefinition> = Object.seal({
  fontFamily: 'Noto Sans',
  fontSize: 43,
  lineHeight: 52,
  color: 'white',
  textAlign: 'center',
  padding: { vertical: 20, horizontal: 32 },
  box: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 16,
    blur: 6.5,
  },
});

// Per-glyph fallbacks appended after any style font so Latin + Chinese both render.
export const SUBTITLE_FONT_FALLBACKS = ['Noto Sans', 'Noto Sans SC'] as const;

// A selectable Google font; `family` is the bare family name (dropdown value + stored fontFamily + lookup key).
export type SubtitleFont = { family: string; googleImport: string };

// Selectable Google fonts for subtitle styles; owned separately from CAPTION_FONTS.
export const SUBTITLE_FONTS: readonly SubtitleFont[] = [
  { family: 'Montserrat', googleImport: 'Montserrat:ital,wght@0,100..900;1,100..900' },
  { family: 'Roboto Condensed', googleImport: 'Roboto+Condensed:wght@100..900' },
  { family: 'Inter', googleImport: 'Inter:opsz,wght@14..32,100..900' },
  { family: 'Open Sans', googleImport: 'Open+Sans:wdth,wght@75..100,300..800' },
  { family: 'Poppins', googleImport: 'Poppins:wght@100;200;300;400;500;600;700;800;900' },
] as const;

// Always imported for Latin + Chinese glyph coverage; not user-selectable.
const NOTO_SUBTITLE_IMPORTS: readonly GoogleFontImport[] = [
  { googleImport: 'Noto+Sans:wght@500' },
  { googleImport: 'Noto+Sans+SC:wght@500' },
];

// Selected Google font (if any) plus the always-present Noto fallbacks.
export const resolveSubtitleImportFonts = (fontFamily?: string): GoogleFontImport[] => {
  const selected = SUBTITLE_FONTS.find((font) => font.family === fontFamily);
  return [...(selected ? [{ googleImport: selected.googleImport }] : []), ...NOTO_SUBTITLE_IMPORTS];
};

export type SubtitleStyleCss = {
  fontFamily: string;
  fontSize?: number;
  lineHeight?: string;
  color?: string;
  textAlign?: 'left' | 'center' | 'right';
  paddingTop?: number;
  paddingBottom?: number;
  paddingLeft?: number;
  paddingRight?: number;
  backgroundColor?: string;
  borderRadius?: number;
  backdropFilter?: string;
};

// CSS string-escape: backslashes and quotes cannot terminate the quoted family name.
const quoteFontFamily = (font: string) => `'${font.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;

// Maps a resolved SubtitleStyleDefinition to CSS. Single source of truth shared by
// the render engine and the admin/editor static previews so they stay in sync.
export const subtitleStyleToCss = (
  style: SubtitleStyleDefinition,
  fontFallbacks: readonly string[] = SUBTITLE_FONT_FALLBACKS
): SubtitleStyleCss => {
  const fonts = [style.fontFamily, ...fontFallbacks].filter((font): font is string => Boolean(font));
  const uniqueFonts = fonts.filter((font, index) => fonts.indexOf(font) === index);

  return {
    fontFamily: uniqueFonts.map(quoteFontFamily).join(', '),
    fontSize: style.fontSize,
    lineHeight: typeof style.lineHeight === 'number' ? `${style.lineHeight}px` : (style.lineHeight ?? undefined),
    color: style.color,
    textAlign: style.textAlign,
    paddingTop: style.padding?.vertical,
    paddingBottom: style.padding?.vertical,
    paddingLeft: style.padding?.horizontal,
    paddingRight: style.padding?.horizontal,
    backgroundColor: style.box?.backgroundColor,
    borderRadius: style.box?.borderRadius,
    backdropFilter: typeof style.box?.blur === 'number' ? `blur(${style.box.blur}px)` : undefined,
  };
};

// Persisted JSON may carry null where the type says undefined: undefined falls through
// to the base value, an explicit null clears it.
const override = <T>(value: T | null | undefined, fallback: T | undefined): T | undefined =>
  value === undefined ? fallback : (value ?? undefined);

// Explicit field-by-field merge: only the known SubtitleStyleDefinition fields are ever read
// from `custom`, so unknown or dangerous keys in the persisted JSON payload (e.g. __proto__)
// can never reach the merged object — no dynamic key iteration, no spread of untrusted data.
export const mergeSubtitleStyle = (custom?: SubtitleStyleDefinition | null): SubtitleStyleDefinition => {
  const base = DEFAULT_SUBTITLE_STYLE;

  if (!custom) {
    return base;
  }

  return {
    fontFamily: override(custom.fontFamily, base.fontFamily),
    fontSize: override(custom.fontSize, base.fontSize),
    lineHeight: override(custom.lineHeight, base.lineHeight),
    color: override(custom.color, base.color),
    textAlign: override(custom.textAlign, base.textAlign),
    padding:
      custom.padding === undefined
        ? base.padding
        : custom.padding === null
          ? undefined
          : {
              vertical: override(custom.padding.vertical, base.padding?.vertical),
              horizontal: override(custom.padding.horizontal, base.padding?.horizontal),
            },
    box:
      custom.box === undefined
        ? base.box
        : custom.box === null
          ? undefined
          : {
              backgroundColor: override(custom.box.backgroundColor, base.box?.backgroundColor),
              borderRadius: override(custom.box.borderRadius, base.box?.borderRadius),
              blur: override(custom.box.blur, base.box?.blur),
            },
  };
};
